/**
 * tutorSlotMoveAdapter — `api/v1/tutor-slot-moves*` → les formes de « garder son tuteur ».
 *
 * Un tuteur arrête un créneau et rien de sa semaine ne tombe dans ceux de la famille : l'élève
 * aura un nouveau tuteur aux mêmes horaires, sauf si la famille réserve, avant l'échéance, des
 * créneaux de ce tuteur. Les heures arrivent déjà dans le fuseau de l'élève (`YYYY-MM-DD HH:mm`),
 * et les libellés écrits par le serveur (« mardi 17h et mardi 18h ») : rien n'est recalculé ici.
 *
 * Une ligne par choix, et un choix par matière : `move_ids` porte ses cours libérés, `hours` les
 * heures perdues, `pick` le nombre de créneaux à choisir, `split` la troisième voie (garder le
 * tuteur pour l'autre matière de son bloc). Un serveur qui ne les sert pas encore se lit comme
 * avant : un cours, une heure, un créneau à choisir, pas de troisième voie.
 *
 * API-aware ; jamais importé par le DS.
 */
import { bookedOutcomeOf } from "../../../ui/formatters/tutorSlotMoveFormatter"
import { toUITutor } from "../LiveSessions/liveSessionListAdapter"
import {
    type UITutorSlotMove,
    type UITutorSlotMoveOption,
    type UITutorSlotMoveOutcome,
    type UITutorSlotMovePick,
    type UITutorSlotMoveSplit,
} from "../../../ui/types/tutorSlotMove"

type ApiTutorSlotMoveSplit = { label?: string | null } | null

/** Une ligne de `GET api/v1/tutor-slot-moves` — seuls les champs lus sont déclarés. */
export type ApiTutorSlotMove = {
    id?: string | number
    move_ids?: (string | number)[] | null
    student_id?: string | number | null
    tutor?: { id?: string | number; firstname?: string; avatar?: string } | null
    freed_day_time?: string | null
    freed_label?: string | null
    hours?: number | null
    deadline?: string | null
    split?: ApiTutorSlotMoveSplit
}

/** Une option de `GET api/v1/tutor-slot-moves/{id}/options`. */
export type ApiTutorSlotMoveOption = {
    key?: string | null
    kind?: string | null
    label?: string | null
    seats_left?: number | null
    effective_on?: string | null
}

export type ApiTutorSlotMoveOptions = {
    keep_label?: string | null
    pick?: { min?: number | null; max?: number | null } | null
    options?: ApiTutorSlotMoveOption[] | null
    split?: ApiTutorSlotMoveSplit
}

/** `POST …/book`, `POST …/keep` et `POST …/split`. */
export type ApiTutorSlotMoveDecision = {
    id?: string | number
    status?: string | null
    outcome?: { label?: string | null; effective_on?: string | null } | null
}

/** Un entier ≥ 1, ou `fallback`. */
function positiveCount(value: unknown, fallback: number): number {
    return typeof value === "number" && Number.isFinite(value) && value >= 1 ? Math.floor(value) : fallback
}

function toUISplit(split: ApiTutorSlotMoveSplit | undefined): UITutorSlotMoveSplit | null {
    return split?.label ? { label: split.label } : null
}

function toUITutorSlotMove(row: ApiTutorSlotMove | null | undefined): UITutorSlotMove | null {
    if (row?.id == null || !row.freed_label || !row.deadline) return null
    const moveIds = Array.isArray(row.move_ids) ? row.move_ids.filter((id) => id != null) : []
    return {
        id: row.id,
        moveIds: moveIds.length > 0 ? moveIds : [row.id],
        tutor: toUITutor(row.tutor ?? null),
        freedLabel: row.freed_label,
        hours: positiveCount(row.hours, 1),
        deadline: row.deadline,
        split: toUISplit(row.split),
    }
}

/**
 * Les choix en attente de CET élève. La réponse est celle du jeton connecté ; le filtre sur
 * `student_id` écarte encore une liste restée d'un autre enfant (une bascule entre la lecture
 * et son affichage).
 */
export function toUITutorSlotMoves(
    rows: ApiTutorSlotMove[] | null | undefined,
    studentId: string | number | null | undefined
): UITutorSlotMove[] {
    return (rows ?? [])
        .filter((row) => row?.student_id == null || studentId == null || String(row.student_id) === String(studentId))
        .map(toUITutorSlotMove)
        .filter((move): move is UITutorSlotMove => move != null)
}

/** Le choix porte-t-il ce cours libéré ? Un lien de mail peut viser n'importe lequel de ses cours. */
export function tutorSlotMoveHolds(move: Pick<UITutorSlotMove, "id" | "moveIds">, moveId: string | number): boolean {
    return String(move.id) === String(moveId) || move.moveIds.some((id) => String(id) === String(moveId))
}

/** Sans `pick` du serveur : un créneau, comme avant les choix par matière. */
const DEFAULT_PICK: UITutorSlotMovePick = { min: 1, max: 1 }

function toUIPick(pick: ApiTutorSlotMoveOptions["pick"]): UITutorSlotMovePick {
    if (pick == null) return DEFAULT_PICK
    const min = positiveCount(pick.min, 1)
    return { min, max: Math.max(min, positiveCount(pick.max, min)) }
}

/**
 * Les options du tuteur, combien en choisir, la troisième voie, et le libellé de « garder les
 * créneaux » — repli écrit d'après le choix tant que le serveur n'a rien dit. Sans `split` dans
 * la réponse, c'est celui de la ligne du choix qui vaut.
 */
export function toUITutorSlotMoveOptions(
    payload: ApiTutorSlotMoveOptions | null | undefined,
    move: Pick<UITutorSlotMove, "freedLabel" | "hours" | "split"> | null
): {
    keepLabel: string
    options: UITutorSlotMoveOption[]
    pick: UITutorSlotMovePick
    split: UITutorSlotMoveSplit | null
} {
    const options = (payload?.options ?? [])
        .map((row): UITutorSlotMoveOption | null =>
            row?.key && row.label && row.effective_on
                ? {
                      key: row.key,
                      kind: row.kind === "new_group" ? "new_group" : "group",
                      label: row.label,
                      seatsLeft: typeof row.seats_left === "number" ? row.seats_left : null,
                      effectiveOn: row.effective_on,
                  }
                : null
        )
        .filter((option): option is UITutorSlotMoveOption => option != null)
    const split = payload != null && payload.split !== undefined ? toUISplit(payload.split) : (move?.split ?? null)
    return { keepLabel: payload?.keep_label || keepLabelFor(move), options, pick: toUIPick(payload?.pick), split }
}

/**
 * « Garder le mardi 19h avec un nouveau tuteur » — ou « les cours du mardi 17h et mardi 18h »
 * sur un choix de plusieurs heures —, écrit d'après le choix tant que le serveur n'a rien dit.
 */
export function keepLabelFor(move: Pick<UITutorSlotMove, "freedLabel" | "hours"> | null): string {
    if (move == null) return "Garder ce créneau avec un nouveau tuteur"
    return move.hours > 1
        ? `Garder les cours du ${move.freedLabel} avec un nouveau tuteur`
        : `Garder le ${move.freedLabel} avec un nouveau tuteur`
}

/**
 * Ce que la réservation a posé : le serveur le dit (`outcome`), et à défaut ce sont les options
 * choisies — le premier cours de l'une d'elles pour la date : l'accusé ne reste jamais sans date.
 */
export function toUITutorSlotMoveBookedOutcome(
    data: ApiTutorSlotMoveDecision | null | undefined,
    chosen: UITutorSlotMoveOption[]
): UITutorSlotMoveOutcome {
    const fallback = bookedOutcomeOf(chosen)
    return {
        ...fallback,
        label: data?.outcome?.label || fallback.label,
        effectiveOn: data?.outcome?.effective_on || fallback.effectiveOn,
    }
}

/** Ce que la troisième voie a posé — le libellé du serveur, et à défaut celui de la carte choisie. */
export function toUITutorSlotMoveSplitOutcome(
    data: ApiTutorSlotMoveDecision | null | undefined,
    split: UITutorSlotMoveSplit | null
): UITutorSlotMoveOutcome {
    return { kind: "split", label: data?.outcome?.label || split?.label || "" }
}
